import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Sword, Trophy, Sparkles, Users, Flame } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOPIC_LABELS = {
  grammar_cloze: 'Grammar Cloze',
  vocabulary_cloze: 'Vocabulary Cloze',
  grammar_mcq: 'Grammar MCQ',
  vocabulary_mcq: 'Vocabulary MCQ',
  synthesis: 'Synthesis & Transformation',
  editing: 'Editing',
  comprehension_cloze: 'Comprehension Cloze',
  antonyms_synonyms: 'Antonyms & Synonyms',
  regular_irregular_verbs: 'Regular & Irregular Verbs'
};

const MONSTER_IMG = {
  dirty: '/monsters/dirty.jpg',
  grey: '/monsters/grey.jpg',
  plastic: '/monsters/plastic.jpg'
};

const MonsterBattlePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [global, setGlobal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAll = async () => {
    try {
      const [s, g] = await Promise.all([
        axios.get(`${API}/monsters/state`, { headers: authHeader() }),
        axios.get(`${API}/monsters/global`)
      ]);
      setState(s.data);
      setGlobal(g.data);
    } catch (err) {
      toast.error('Failed to load monster battle');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const selectMonster = async (key) => {
    if (!state || state.active_monster === key) return;
    setSwitching(true);
    try {
      await axios.post(`${API}/monsters/select`, { monster: key }, { headers: authHeader() });
      toast.success(`Now attacking ${state.monsters[key].name}!`);
      await fetchAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : (detail || 'Cannot switch monsters right now');
      toast.error(msg);
    } finally {
      setSwitching(false);
    }
  };

  if (loading || !state) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <p className="text-slate-600">Loading your battle…</p>
      </div>
    );
  }

  const active = state.monsters[state.active_monster];
  const damagePct = Math.round((active.total_damage / active.total_hp) * 100);
  const earnedBadgeKeys = state.badges.filter(b => b.earned).map(b => b.key);
  const totalDefeats = state.defeats.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-[#EDF4FF]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-slate-100 rounded-full"
            data-testid="monsters-back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="font-heading font-black text-xl text-slate-900">Monster Battle</h1>
            <p className="text-xs text-slate-500">Practise to fight for a cleaner Singapore.</p>
          </div>
          {global && (
            <div className="hidden md:flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5 text-xs" data-testid="global-defeats-badge">
              <Users size={12} className="text-emerald-700" />
              <span className="font-bold text-emerald-800">{global.total_defeats.toLocaleString()}</span>
              <span className="text-emerald-700">monsters defeated by students</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Active monster hero */}
        <Card className="p-6 md:p-8 rounded-3xl border-2 shadow-lg mb-6" style={{ borderColor: active.accent }}>
          <div className="grid md:grid-cols-[280px,1fr] gap-6 md:gap-8 items-center">
            <div className="relative">
              <motion.img
                key={state.active_monster}
                src={MONSTER_IMG[state.active_monster]}
                alt={active.name}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 - Math.min(0.5, damagePct / 200) }}
                transition={{ duration: 0.4 }}
                className="w-full aspect-square object-contain rounded-2xl"
                data-testid={`monster-img-${state.active_monster}`}
              />
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white border-2 rounded-full px-4 py-1 shadow-md font-black text-sm" style={{ borderColor: active.accent, color: active.colour }}>
                {damagePct}%
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: active.accent }}>
                Cause · {active.cause}
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-black text-slate-900 mb-2">
                {active.name}
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-4 max-w-lg">{active.tagline}</p>
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-bold text-slate-700">Total damage</span>
                  <span className="font-black" style={{ color: active.colour }}>
                    {active.total_damage} / {active.total_hp}
                  </span>
                </div>
                <Progress value={damagePct} className="h-3" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full px-5"
                  data-testid="monsters-cta-practice"
                >
                  <Sword size={16} className="mr-1.5" /> Practise to attack
                </Button>
                {totalDefeats > 0 && (
                  <div className="flex items-center gap-1 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
                    <Trophy size={12} className="text-amber-600" />
                    <span className="font-bold text-amber-800">{totalDefeats}</span> defeated
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Per-topic HP bars */}
        <Card className="p-6 rounded-3xl border-2 border-slate-200 mb-6" data-testid="monsters-topic-hp">
          <h3 className="font-heading font-black text-lg text-slate-900 mb-1">
            Topic damage · {active.name}
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Each topic must reach {state.points_per_topic_to_defeat} damage points to defeat the monster.
            You'll get bonus damage every time you unlock a badge.
          </p>
          <div className="space-y-3">
            {state.topics_order.map((topic) => {
              const pts = active.topic_points[topic] || 0;
              const pct = Math.round((pts / state.points_per_topic_to_defeat) * 100);
              const complete = pts >= state.points_per_topic_to_defeat;
              return (
                <div key={topic} data-testid={`topic-hp-${topic}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                      {complete && <span className="text-emerald-600 text-lg leading-none">✓</span>}
                      {TOPIC_LABELS[topic]}
                    </span>
                    <span className={`text-sm font-black ${complete ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {pts} / {state.points_per_topic_to_defeat}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${complete ? 'bg-emerald-500' : ''}`}
                      style={{
                        width: `${pct}%`,
                        backgroundColor: complete ? undefined : active.accent
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Badges */}
        <Card className="p-6 rounded-3xl border-2 border-slate-200 mb-6" data-testid="monsters-badges">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-black text-lg text-slate-900 flex items-center gap-2">
                <Sparkles size={18} className="text-amber-500" /> Badges
              </h3>
              <p className="text-xs text-slate-500">
                Each badge deals {state.badge_damage_multiplier}× extra damage to your active monster.
              </p>
            </div>
            <div className="text-sm text-slate-600">
              <span className="font-black text-emerald-700">{earnedBadgeKeys.length}</span>
              <span className="text-slate-400"> / {state.badges.length} earned</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {state.badges.map((b) => (
              <div
                key={b.key}
                className={`p-4 rounded-2xl border-2 text-center transition-all ${
                  b.earned
                    ? 'bg-amber-50 border-amber-300 shadow-sm'
                    : 'bg-slate-50 border-slate-200 opacity-60 grayscale'
                }`}
                data-testid={`badge-${b.key}`}
              >
                <div className="text-3xl mb-1" aria-hidden="true">{b.emoji}</div>
                <div className="font-heading font-bold text-sm text-slate-900">{b.name}</div>
                <p className="text-[10px] text-slate-500 leading-snug mt-1">{b.description}</p>
                {b.earned && (
                  <div className="text-[10px] font-bold text-amber-700 mt-1">EARNED ✓</div>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Choose your battle */}
        <Card className="p-6 rounded-3xl border-2 border-slate-200 mb-6" data-testid="monsters-choose">
          <h3 className="font-heading font-black text-lg text-slate-900 mb-1">Choose your battle</h3>
          <p className="text-xs text-slate-500 mb-4">
            Switching monsters is only allowed if your active monster has 0 damage. Finish what you started first!
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {['dirty', 'grey', 'plastic'].map((key) => {
              const m = state.monsters[key];
              const isActive = key === state.active_monster;
              const defeats = m.defeats_count || 0;
              return (
                <button
                  key={key}
                  onClick={() => selectMonster(key)}
                  disabled={switching || isActive}
                  className={`text-left p-4 rounded-2xl border-2 transition-all ${
                    isActive
                      ? 'border-emerald-500 bg-emerald-50 cursor-default'
                      : 'border-slate-200 bg-white hover:border-slate-400 hover:shadow-md'
                  }`}
                  data-testid={`choose-monster-${key}`}
                >
                  <div className="flex items-center gap-3">
                    <img src={MONSTER_IMG[key]} alt={m.name} className="w-14 h-14 object-contain rounded-lg" />
                    <div className="flex-1">
                      <div className="font-heading font-bold text-sm text-slate-900">{m.name}</div>
                      <div className="text-[10px] text-slate-500">{m.cause}</div>
                      <div className="text-[10px] mt-1 flex items-center gap-2">
                        <span className="font-bold" style={{ color: m.colour }}>{m.total_damage}/{m.total_hp}</span>
                        {defeats > 0 && (
                          <span className="flex items-center gap-0.5 text-amber-700">
                            <Trophy size={10} /> {defeats}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isActive && (
                    <div className="mt-2 text-[10px] font-black text-emerald-700 uppercase tracking-wide">
                      Currently attacking
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Global community counter */}
        {global && (
          <Card className="p-6 rounded-3xl border-2 border-emerald-200 bg-emerald-50" data-testid="monsters-global-card">
            <h3 className="font-heading font-black text-lg text-slate-900 mb-3 flex items-center gap-2">
              <Flame size={18} className="text-emerald-600" /> Prof.Scaly students are making Singapore better
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {['dirty', 'grey', 'plastic'].map((key) => (
                <div key={key} className="text-center">
                  <img src={MONSTER_IMG[key]} alt={global.by_monster[key].name} className="w-12 h-12 mx-auto object-contain rounded-lg opacity-60" />
                  <div className="text-2xl font-heading font-black text-slate-900 mt-1">
                    {global.by_monster[key].defeats.toLocaleString()}
                  </div>
                  <div className="text-[10px] text-slate-600 leading-tight">{global.by_monster[key].cause}</div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
};

export default MonsterBattlePage;
